import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { formatDisplayDate } from "../lib/format.ts";
import { buildPalette, type Group, type Ranked, segments, splitQuery } from "../lib/palette.ts";
import { useAppState } from "./hooks.ts";
import { Icon } from "./icon.tsx";
import type { IconName } from "./icon-paths.ts";
import type { NewMode } from "./new-form.tsx";

const mod =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "Cmd" : "Ctrl";

interface ActionItem {
  id: string;
  label: string;
  icon: IconName;
  /** A few words after the name. */
  hint?: string;
  /** Shortcut shown at the right. */
  key?: string;
  /** For switches and the current mode: whether it is on. */
  on?: boolean;
  /** Shown before anything is typed. */
  common?: boolean;
  run: () => void;
}

interface NoteItem {
  path: string;
  label: string;
  alt: string;
  meta: string;
}

interface HeadingItem {
  index: number;
  label: string;
  level: number;
}

/** What the palette can do right now; things that need an open note or Focus are left out without one. */
function actionsFor(
  app: App,
  s: ReturnType<App["state"]["get"]>,
  begin: (mode: Exclude<NewMode, null>) => void,
  canFocus: boolean,
): ActionItem[] {
  const has = Boolean(s.current);
  const dark = app.effectiveTheme() === "dark";
  const list: (ActionItem | false)[] = [
    {
      id: "live",
      label: "Live Markdown",
      icon: "live",
      hint: "Writing mode",
      on: s.editorId === "lexical-live",
      common: true,
      run: () => app.setEditorId("lexical-live"),
    },
    {
      id: "rich",
      label: "Rich text",
      icon: "rich",
      hint: "Writing mode",
      on: s.editorId === "rich",
      common: true,
      run: () => app.setEditorId("rich"),
    },
    {
      id: "plain",
      label: "Plain text",
      icon: "plain",
      hint: "Raw Markdown",
      on: s.editorId === "plain",
      common: true,
      run: () => {
        if (app.state.get().editorId !== "plain") app.toggleRaw();
      },
    },
    has &&
      canFocus && {
        id: "focus",
        label: "Focus",
        icon: "focus",
        key: `${mod}+Shift+F`,
        on: s.focus,
        common: true,
        run: () => app.setFocus(!app.state.get().focus),
      },
    has &&
      canFocus && {
        id: "typewriter",
        label: "Typewriter",
        icon: "typewriter",
        hint: "Turns Focus on too",
        on: s.focus && s.typewriter,
        run: () => {
          const now = app.state.get();
          const next = !(now.focus && now.typewriter);
          if (next && !now.focus) app.setFocus(true);
          app.setTypewriter(next);
        },
      },
    {
      id: "dark",
      label: "Dark theme",
      icon: "moon",
      on: dark,
      run: () => app.toggleTheme(),
    },
    has && {
      id: "details",
      label: "Details",
      icon: "info",
      hint: "Front matter",
      run: () => app.setDetails(true),
    },
    has && {
      id: "preview",
      label: "Preview as the site",
      icon: "preview",
      run: () => app.setPreview(true),
    },
    has && {
      id: "export",
      label: "Export this note",
      icon: "export",
      common: true,
      run: () => app.exportCurrent(),
    },
    s.docs.length > 0 && {
      id: "export-all",
      label: "Export everything (.zip)",
      icon: "export",
      run: () => app.exportAll(),
    },
    has && {
      id: "rename",
      label: "Rename this file",
      icon: "rename",
      common: true,
      run: () => app.requestRename(),
    },
    {
      id: "new-thought",
      label: "New thought",
      icon: "plus",
      common: true,
      run: () => begin("thought"),
    },
    {
      id: "new-document",
      label: "New document",
      icon: "newdoc",
      run: () => begin("document"),
    },
    {
      id: "files",
      label: s.sidebarOpen ? "Hide files" : "Show files",
      icon: "sidebar",
      key: `${mod}+\\`,
      run: () => app.toggleSidebar(),
    },
  ];
  return list.filter((a): a is ActionItem => a !== false);
}

function notesFor(s: ReturnType<App["state"]["get"]>): { all: NoteItem[]; recent: NoteItem[] } {
  const all = [...s.docs]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((d) => ({
      path: d.path,
      label: d.title || d.path,
      alt: d.path,
      meta: [d.isThought ? "Thought" : "Document", d.date && formatDisplayDate(d.date)]
        .filter(Boolean)
        .join(" · "),
    }));
  return { all, recent: all.filter((n) => n.path !== s.current?.path) };
}

type Row = Group<ActionItem, NoteItem, HeadingItem>["rows"][number];

function Label({ text, ranked }: { text: string; ranked: Ranked<unknown> }) {
  return (
    <>
      {segments(text, ranked.marks).map((p, i) =>
        p.mark ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>,
      )}
    </>
  );
}

/** One box for actions, notes and the headings of the open note. Opens with Ctrl+Shift+K. */
export function Palette({
  app,
  begin,
  canFocus,
}: {
  app: App;
  begin: (mode: Exclude<NewMode, null>) => void;
  canFocus: boolean;
}) {
  const s = useAppState(app);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  // Layout effects run before the browser handles another key, so nothing typed on opening is lost.
  useLayoutEffect(() => {
    if (s.paletteOpen) {
      opener.current = document.activeElement;
      input.current?.focus();
    } else {
      setQuery("");
      setActive(0);
    }
  }, [s.paletteOpen]);

  const notes = s.paletteOpen ? notesFor(s) : { all: [], recent: [] };
  const groups = s.paletteOpen
    ? buildPalette<ActionItem, NoteItem, HeadingItem>({
        query,
        actions: actionsFor(app, s, begin, canFocus),
        notes: notes.all,
        recent: notes.recent,
        headings: s.headings.map((h, index) => ({
          index,
          label: h.text || "(empty heading)",
          level: h.level,
        })),
      })
    : [];
  const rows: Row[] = groups.flatMap((g) => g.rows);
  const at = Math.min(active, Math.max(rows.length - 1, 0));
  const { q } = splitQuery(query);

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [at, query]);

  if (!s.paletteOpen) return null;

  /** Closes the palette, puts the caret back, and only then does what was chosen. */
  const finish = (then?: () => void) => {
    app.setPalette(false);
    const el = opener.current;
    if (el instanceof HTMLElement && el.isConnected && !el.closest(".editor-host")) el.focus();
    else app.focusEditor();
    then?.();
  };

  const choose = (row: Row | undefined) => {
    if (!row) return;
    if (row.kind === "action") finish(row.ranked.item.run);
    else if (row.kind === "note") finish(() => app.open(row.ranked.item.path));
    else finish(() => app.goToHeading(row.ranked.item.index));
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      finish();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length)
        setActive((at + (e.key === "ArrowDown" ? 1 : -1) + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(rows[at]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      input.current?.focus();
    }
  };

  const typed = (value: string) => {
    setQuery(value);
    setActive(0);
  };

  let index = -1;
  return (
    <>
      <div class="palette-scrim" onPointerDown={() => finish()} />
      <div
        class="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKey}
      >
        <div class="palette-search">
          <Icon name="search" />
          <input
            ref={input}
            type="text"
            role="combobox"
            aria-label="Search notes and commands"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-autocomplete="list"
            aria-activedescendant={rows.length ? `palette-opt-${at}` : undefined}
            placeholder="[search notes or commands]"
            autocomplete="off"
            spellcheck={false}
            value={query}
            onInput={(e) => typed(e.currentTarget.value)}
          />
          <kbd>Esc</kbd>
        </div>
        <div class="palette-modes">
          {[
            ["", "Everything"],
            [">", "> Actions"],
            ["#", "# This note's headings"],
          ].map(([prefix, label]) => (
            <button
              key={label}
              class="palette-chip"
              type="button"
              aria-pressed={(query[0] === ">" ? ">" : query[0] === "#" ? "#" : "") === prefix}
              onClick={() => {
                typed(prefix!);
                input.current?.focus();
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          class="palette-list"
          id="palette-list"
          role="listbox"
          aria-label="Results"
          ref={list}
          onMouseDown={(e) => e.preventDefault()}
        >
          {groups.map((g) => (
            // biome-ignore lint/a11y/useSemanticElements: a group inside a listbox, not a form fieldset
            <div key={g.id} role="group" aria-labelledby={`palette-group-${g.id}`}>
              <p class="palette-group" id={`palette-group-${g.id}`}>
                {g.title}
              </p>
              {g.rows.map((row) => {
                index += 1;
                const i = index;
                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard use goes through the search box
                  <div
                    key={`${row.kind}-${i}`}
                    class="palette-row"
                    role="option"
                    id={`palette-opt-${i}`}
                    tabIndex={-1}
                    aria-selected={i === at}
                    onClick={() => choose(row)}
                    onMouseMove={() => i !== at && setActive(i)}
                  >
                    {row.kind === "action" && (
                      <>
                        <span class="palette-lead">
                          <Icon name={row.ranked.item.icon} />
                        </span>
                        <span class="palette-name">
                          <Label text={row.ranked.item.label} ranked={row.ranked} />
                          {row.ranked.item.hint && <small>{row.ranked.item.hint}</small>}
                        </span>
                        <span class="palette-tail">
                          {row.ranked.item.on !== undefined && (
                            <span class={row.ranked.item.on ? "palette-state on" : "palette-state"}>
                              {row.ranked.item.on ? "On" : "Off"}
                            </span>
                          )}
                          {row.ranked.item.key && <kbd>{row.ranked.item.key}</kbd>}
                        </span>
                      </>
                    )}
                    {row.kind === "note" && (
                      <>
                        <span class="palette-lead">
                          <Icon name="note" />
                        </span>
                        <span class="palette-name">
                          <Label text={row.ranked.item.label} ranked={row.ranked} />
                          {row.ranked.item.label !== row.ranked.item.path && (
                            <small>{row.ranked.item.path}</small>
                          )}
                        </span>
                        <span class="palette-tail">{row.ranked.item.meta}</span>
                      </>
                    )}
                    {row.kind === "heading" && (
                      <>
                        <span class="palette-lead">
                          <Icon name="hash" />
                        </span>
                        <span class="palette-name">
                          <Label text={row.ranked.item.label} ranked={row.ranked} />
                          <small>Heading {row.ranked.item.level}</small>
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {rows.length === 0 && (
            <p class="palette-empty">
              {q
                ? `Nothing matches “${q}”. Try fewer letters, or clear the box to see recent notes.`
                : "Nothing here yet. Open a note to see its headings."}
            </p>
          )}
        </div>
        <div class="palette-foot">
          <span class="palette-keys">Up and Down to move</span>
          <span class="palette-keys">Enter to choose</span>
          <span role="status" aria-live="polite">
            {rows.length === 0
              ? "No results"
              : `${rows.length} ${rows.length === 1 ? "result" : "results"}`}
          </span>
        </div>
      </div>
    </>
  );
}
